import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/i18n/useTranslation";
import { calculateScriptFormScore, getScoreColor } from "@/features/dashboard/utils";
import type { AvailableForm, Script, ScriptForm } from "@/features/dashboard/types";
import { Plus, ExternalLink, MoreHorizontal, ChevronDown, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVersionSelection } from "@/features/versions/context/VersionSelectionContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface MobileScriptViewProps {
  scripts: Script[];
  formatDate: (dateString: string) => string;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  onAddForm: (script: Script, formId: string) => Promise<void>;
  availableFormsByScript: Record<string, AvailableForm[]>;
  addingForm: { scriptId: string; formId: string } | null;
  onOpenVersionControl?: (scriptId: string) => void;
  onRefresh?: () => Promise<void>;
}

const FORM_ORDER = [
  "Concept & Pitch",
  "Screenplay & Treatment",
  "Project Development & Financing",
];

export const MobileScriptView = ({
  scripts,
  formatDate,
  onShare,
  onAnalyze,
  onAddForm,
  availableFormsByScript,
  addingForm,
  onOpenVersionControl,
  onRefresh,
}: MobileScriptViewProps) => {
  const { t, language } = useTranslation();
  const { getSelectedVersion, setSelectedVersion } = useVersionSelection();
  const [selectedFormByScript, setSelectedFormByScript] = useState<Record<string, string>>({});

  // Get form metadata from both existing forms and available forms
  const formMetadata = scripts
    .flatMap((script) => script.script_forms)
    .reduce((acc, sf) => {
      if (!acc[sf.feedback_forms.id]) {
        acc[sf.feedback_forms.id] = {
          id: sf.feedback_forms.id,
          name: sf.feedback_forms.name,
          name_th: sf.feedback_forms.name_th,
        };
      }
      return acc;
    }, {} as Record<string, { id: string; name: string; name_th: string | null }>);

  // Add metadata from available forms
  Object.values(availableFormsByScript).forEach((forms) => {
    forms.forEach((form) => {
      if (!formMetadata[form.id]) {
        formMetadata[form.id] = {
          id: form.id,
          name: form.name,
          name_th: form.name_th,
        };
      }
    });
  });

  return (
    <div className="space-y-4 pb-20">
      {scripts.map((script) => {
        const activeVersionId = getSelectedVersion(script.id) || script.current_version_id;
        const filteredForms = script.script_forms.filter(
          (form) => form.script_version_id === activeVersionId
        );
        
        const formsGrouped = filteredForms.reduce((acc, form) => {
          const formId = form.feedback_forms.id;
          if (!acc[formId]) {
            acc[formId] = form;
          }
          return acc;
        }, {} as Record<string, ScriptForm>);

        const availableForms = availableFormsByScript[script.id] || [];

        // Get form types relevant to this specific script (attached + available)
        const scriptFormTypes = new Set<string>();
        filteredForms.forEach((form) => scriptFormTypes.add(form.feedback_forms.id));
        availableForms.forEach((form) => scriptFormTypes.add(form.id));
        
        // Sort forms according to FORM_ORDER
        const scriptFormTypesArray = Array.from(scriptFormTypes).sort((a, b) => {
          const nameA = formMetadata[a]?.name || "";
          const nameB = formMetadata[b]?.name || "";
          const indexA = FORM_ORDER.indexOf(nameA);
          const indexB = FORM_ORDER.indexOf(nameB);
          
          // If both are in FORM_ORDER, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only A is in FORM_ORDER, it comes first
          if (indexA !== -1) return -1;
          // If only B is in FORM_ORDER, it comes first
          if (indexB !== -1) return 1;
          // If neither is in FORM_ORDER, sort alphabetically
          return nameA.localeCompare(nameB);
        });

        return (
          <Card key={script.id} className="overflow-hidden border border-slate-200/90 bg-white/95 backdrop-blur-md shadow-sm rounded-2xl">
            <CardContent className="p-4 space-y-4">
              {/* Script Header */}
              <div className="space-y-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate">
                      {script.movie_name}
                    </h3>
                    {script.custom_message && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {script.custom_message}
                      </p>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {t("dashboard.table.script_column")} • {formatDate(script.created_at)}
                    </div>
                  </div>

                  {/* Version Info - Right Side */}
                  <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1 min-h-[44px] px-3 w-full sm:w-auto"
                        >
                          <span>v{script.script_versions?.find((v) => v.id === activeVersionId)?.version_number || 1}</span>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {script.script_versions?.map((version) => {
                          const isSelected = version.id === activeVersionId;
                          const isCurrent = version.id === script.current_version_id;
                          
                          return (
                            <DropdownMenuItem
                              key={version.id}
                              onClick={() => setSelectedVersion(script.id, version.id)}
                              className={cn(
                                "flex items-center justify-between cursor-pointer",
                                "data-[highlighted]:text-white",
                                isSelected && "bg-primary text-white"
                              )}
                            >
                              <div className="flex flex-col">
                                <span className={cn(
                                  "font-medium data-[highlighted]:text-white",
                                  isSelected && "text-white"
                                )}>
                                  v{version.version_number}
                                  {version.version_name && `: ${version.version_name}`}
                                </span>
                                {isCurrent && (
                                  <span className={cn(
                                    "text-xs data-[highlighted]:text-white/90",
                                    isSelected ? "text-white/80" : "text-muted-foreground"
                                  )}>
                                    {t("versions.current_badge")}
                                  </span>
                                )}
                              </div>
                              {isSelected && <span className="text-white">✓</span>}
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onOpenVersionControl?.(script.id)}
                          className="cursor-pointer data-[highlighted]:text-white"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          {t("versions.title")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              {/* Horizontally Scrollable Form Pills */}
              {scriptFormTypesArray.length > 0 && (() => {
                const selectedFormId = selectedFormByScript[script.id] || scriptFormTypesArray[0];
                const visibleForms = scriptFormTypesArray.slice(0, 4);
                const overflowForms = scriptFormTypesArray.slice(4);
                const hasOverflow = overflowForms.length > 0;

                return (
                  <div className="space-y-4">
                    {/* Pill Bar */}
                    <div className="flex items-center gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
                      {visibleForms.map((formId) => {
                        const meta = formMetadata[formId];
                        const displayName = language === "th" && meta?.name_th ? meta.name_th : meta?.name;
                        const isSelected = selectedFormId === formId;
                        
                        return (
                          <button
                            key={formId}
                            onClick={() => setSelectedFormByScript({ ...selectedFormByScript, [script.id]: formId })}
                            className={cn(
                              "snap-start flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px]",
                              isSelected
                                ? "bg-gradient-primary text-white shadow-medium hover:opacity-90"
                                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                            )}
                          >
                            {displayName}
                          </button>
                        );
                      })}
                      
                      {hasOverflow && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={cn(
                                "snap-start flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px] flex items-center gap-1",
                                overflowForms.includes(selectedFormId)
                                  ? "bg-gradient-primary text-white shadow-medium hover:opacity-90"
                                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                              )}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              {t("dashboard.mobile.more")}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {overflowForms.map((formId) => {
                              const meta = formMetadata[formId];
                              const displayName = language === "th" && meta?.name_th ? meta.name_th : meta?.name;
                              
                              return (
                                <DropdownMenuItem
                                  key={formId}
                                  onClick={() => setSelectedFormByScript({ ...selectedFormByScript, [script.id]: formId })}
                                >
                                  {displayName}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {/* Selected Form Content */}
                    {(() => {
                      const scriptForm = formsGrouped[selectedFormId];
                      const meta = formMetadata[selectedFormId];
                      const displayName = language === "th" && meta?.name_th ? meta.name_th : meta?.name;
                      const canAdd = availableForms.some((f) => f.id === selectedFormId);
                      const isAddingThis = addingForm?.scriptId === script.id && addingForm?.formId === selectedFormId;

                      return (
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm">{displayName}</h4>

                          {scriptForm ? (
                            <ScoreMobileCard
                              script={script}
                              scriptForm={scriptForm}
                              score={calculateScriptFormScore(scriptForm)}
                              onShare={onShare}
                              onAnalyze={onAnalyze}
                              language={language}
                            />
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full min-h-[44px]"
                              disabled={!canAdd || !!addingForm}
                              onClick={() => canAdd && onAddForm(script, selectedFormId)}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {isAddingThis ? t("dashboard.table.add_form_hint") + "..." : t("dashboard.table.add_form_hint")}
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

interface ScoreMobileCardProps {
  script: Script;
  scriptForm: ScriptForm;
  score: number | null;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  language: string;
}

const ScoreMobileCard = ({
  script,
  scriptForm,
  score,
  onShare,
  onAnalyze,
  language,
}: ScoreMobileCardProps) => {
  const { t } = useTranslation();
  const hasScore = typeof score === "number" && !Number.isNaN(score);
  const colorClass = hasScore ? getScoreColor(score) : "text-muted-foreground";
  const scoreValue = hasScore ? score.toFixed(1) : "—";
  const reviewCount = scriptForm.feedback_submissions?.length ?? 0;
  
  const reviewText = language === "th" 
    ? `${reviewCount} รีวิว`
    : reviewCount === 1 
      ? `${reviewCount} review` 
      : `${reviewCount} reviews`;

  return (
    <div className="rounded-xl border border-border/80 bg-card p-4 space-y-3 shadow-2xs">
      {/* Score Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <div className={cn("text-3xl font-extrabold tabular-nums tracking-tight", colorClass)}>
            {scoreValue}
            {hasScore && <span className="text-sm font-normal text-muted-foreground ml-1">/ 5</span>}
          </div>
          <p className="text-xs text-muted-foreground font-medium">
            {reviewText}
          </p>
        </div>
      </div>

      {/* Direct Action Row */}
      <div className="pt-2 border-t border-border/60 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onShare(script, scriptForm)}
          className="flex-1 gap-1.5 min-h-[44px] rounded-xl text-xs font-semibold"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t("dashboard.table.share_with_team")}
        </Button>
        {hasScore && (
          <Button
            variant="default"
            size="sm"
            onClick={() => onAnalyze(scriptForm, script.movie_name, script)}
            className="flex-1 min-h-[44px] rounded-xl text-xs font-semibold"
          >
            {t("dashboard.table.open_analytics")}
          </Button>
        )}
      </div>
    </div>
  );
};
